import React, { useRef, useState, useEffect } from "react";
import { useTranslation } from "context/TranslationProvider";
import BootstrapTable from "react-bootstrap-table-next";
import Pagination from "../components/Pagination";
import DropdownFilter from "utils/DropdownFilter";
import ToolkitProvider from "react-bootstrap-table2-toolkit/dist/react-bootstrap-table2-toolkit";
import senderLogo from "assets/img/images/sender.svg";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import { Avatar} from "@mui/material";
import constants from "constants";
import {
  Button,
  UncontrolledTooltip,
  Modal,
} from "reactstrap";

import { Switch } from "@mui/material";
import "assets/css/custom.css";
import helpers from "utils/helpers";
import useNotificationHighlight from "hooks/useNotificationHighlight";
import UserActions from "./components/UserActions";
import RoleModal from "./RoleModal";
import { useAuth } from "context/AuthContext";
import DeactivateReasonModal from "modals/DeactivateReasonModal";
import VerifiedIcon from "@mui/icons-material/Verified";
import CancelIcon from "@mui/icons-material/Cancel";
import CloseIcon from "@mui/icons-material/Close";

function UsersTable({
  items,
  setItems,
  handleToggleAccount,
  userActions,
  onTableChange,
  tableState,
  setTableState,
  meta,
  filterType,
  setFilterType,
  statusLoadingId,
  setStatusLoadingId,
  isItemsLoading,
}) {
  const { t, language } = useTranslation();
  const { isHighlighted } = useNotificationHighlight();
  const dataTable = items || [];
  const componentRef = useRef(null);
  const [assignRole, setAssignRole] = useState(null);
  const [isRoleModalOpen, setRoleModalOpen] = useState(false);
  const { permission } = useAuth();
  
  const [localSearch, setLocalSearch] = useState(tableState.search || "");

  useEffect(() => {
    const handler = setTimeout(() => {
      setTableState((prev) => {
        const prevSearch = prev.search || "";
        if (prevSearch === localSearch) {
          return prev;
        }
        return {
          ...prev,
          search: localSearch,
          page: 1,
        };
      });
    }, 500);
    return () => clearTimeout(handler);
  }, [localSearch, setTableState]);

  const handleClearFilters = () => {
    setLocalSearch("");
    setFilterType("All Users");
    setTableState((prev) => ({
      ...prev,
      search: "",
      page: 1,
    }));
  };


  const canUser = permission?.permissions?.modules?.users
  const openAssignModal = (ticket) => {
    setAssignRole(ticket);
    setRoleModalOpen(true);
  };

  const closeRoleModal = () => {
    setAssignRole(null);
    setRoleModalOpen(false);
  };

  const [reasonModal, setReasonModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const commonColumnProps = {
  headerClasses: "table-header",
  classes: "table-cell",
};

  return (
    <>
      <ToolkitProvider
        data={dataTable}
        key={language}
        keyField="_id"
        columns={[
          {
            dataField: "name",
            text: t("table.headers.name", "Name"),
            sort: true,
            headerAlign: "left",
            align: "left",
            ...commonColumnProps,
            formatter: (cell, row) => {
              const profileFile = row?.profilePictureUrl;
              const picUrl = `${constants.s3Bucket}/profile-pics/${profileFile}`;
              const isVerified = row?.isVerified === true;

              const tooltipId = `status-${row._id}`;

              return (
                <div className="d-flex align-items-center ">
                  <span className="d-flex align-items-center">
                    {profileFile && profileFile !== "undefined" ? (
                      <Avatar
                        src={picUrl}
                        sx={{ width: 32, height: 32, cursor: "pointer" }}
                        onClick={() => userActions?.handleView(row)}
                      />
                    ) : (
                      <Avatar
                        {...helpers.stringAvatar(
                          `${row?.firstName || ""} ${row?.lastName || ""}`
                        )}
                        sx={{
                          width: 30,
                          height: 30,
                          fontSize: 14,
                          cursor: "pointer",
                        }}
                        onClick={() => userActions?.handleView(row)}
                      />
                    )}
                  </span>
                  <span
                    id={`view-${row._id}`}
                    className="ml-2 cursor-pointer text-primary"
                    onClick={() => userActions?.handleView(row)}
                  >
                    {cell}
                  </span>
                  <UncontrolledTooltip target={`view-${row._id}`}>
                    {t("page.user-profile.tooltip.viewDetails", "View Details")}
                  </UncontrolledTooltip>
                  <span className="d-flex align-items-center">
                    {isVerified ? (
                      <>
                        <VerifiedIcon
                          id={tooltipId}
                          className="icon-status verifiedIcon"
                        />
                        <UncontrolledTooltip target={tooltipId}>
                          {t("page.user-profile.tooltip.verifiedUser", "Verified User")}
                        </UncontrolledTooltip>
                      </>
                    ) : (
                      <>
                        <CancelIcon
                          id={tooltipId}
                          className="icon-status unverifiedIcon"
                        />
                        <UncontrolledTooltip target={tooltipId}>
                          {t("page.user-profile.tooltip.unverifiedUser", "Unverified User")}
                        </UncontrolledTooltip>
                      </>
                    )}
                  </span>
                </div>
              );
            },
          },
          {
            dataField: "email",
            text: t("table.headers.email", "Email"),
            headerAlign: "left",
            align: "left",
            sort: true,
            ...commonColumnProps,
          },
          {
            dataField: "role",
            text: t("table.headers.role", "Role"),
            headerAlign: "left",
            align: "left",
            headerStyle: { textAlign: "left", verticalAlign: "middle" },
            style: { textAlign: "left", verticalAlign: "middle" },
            ...commonColumnProps,
            formatter: (_, row, rowIndex, formatExtraData) => {
              const statusLoadingId = formatExtraData;
              const isDeleted = row?.isDeleted;
              return (
                <div className="d-flex align-items-center justify-content-start text-left w-100">
                  <span id={`assign-wrapper-${row._id}`}>
                    <Button
                      id={`assign-${row._id}`}
                      color="link"
                      onClick={() => canUser?.update && openAssignModal(row)}
                      disabled={!canUser?.update || isDeleted || statusLoadingId === row._id}
                      className={`p-0 text-left ${(!canUser?.update || isDeleted || statusLoadingId === row._id) ? "no-pointer" : ""}`}
                      style={{ textAlign: "left" }}
                    >
                      {row.role.toLowerCase() === "super admin"
                        ? t("table.row.super_admin", "Super Admin")
                        : t(`table.row.${row.role.toLowerCase()}`)}
                    </Button>
                  </span>
      
                  <UncontrolledTooltip
                    key={`assign-${row._id}-${language}`}
                    target={`assign-wrapper-${row._id}`}
                    trigger="hover"
                  >
                    {isDeleted ? t("page.users.actions.userDeleted", "User is deleted") : t("tooltip.changeRole", "Click to change assigned role")}
                  </UncontrolledTooltip>
                </div>
              );
            },
            formatExtraData: statusLoadingId,
          },
          {
            dataField: "accountStatus",
            text: t("table.headers.status", "Status"),
            headerAlign: "left",
            align: "left",
            ...commonColumnProps,
            sort: true,
            formatter: (cell, row, rowIndex, formatExtraData) => {
              const statusLoadingId = formatExtraData;
              const status = row?.accountStatus || "pending";
              const isActive = status === "active";
              const isDeleted = row?.isDeleted;
              return (
                <div className="d-flex align-items-center w-100" id={`status-wrapper-${row._id}`}>
                <Switch
                  size="small"
                  checked={isActive}
                  disabled={!canUser?.update || isDeleted || statusLoadingId === row._id}
                  className={`${(!canUser?.update || isDeleted) ? "no-pointer" : ""}`}
                  onChange={(e) => {
                    if (!canUser?.update) return;

                    if (e.target.checked) {
                      handleToggleAccount(row, true);
                    } else {
                      setStatusLoadingId(row._id);
                      setSelectedUser(row);
                      setReasonModal(true);
                    }
                  }}
                />
                {isDeleted && (
                  <UncontrolledTooltip target={`status-wrapper-${row._id}`}>
                    {t("page.users.actions.userDeleted", "User is deleted")}
                  </UncontrolledTooltip>
                )}
                </div>
              );
            },
            formatExtraData: statusLoadingId,
          },
          {
            text: t("table.headers.listings", "Listings"),
            dataField: "listings",
            headerAlign: "left",
            align: "left",
            ...commonColumnProps,
            sort: false,
            formatter: (cell, row) => {
              const traveler = row?.listings?.travelerCount ?? 0;
              const sender = row?.listings?.senderCount ?? 0;

              return (
                <div className="d-flex listings-wrapper">
                  <div className="d-flex flex-column align-items-center">
                    <div
                      id={`traveler-${row._id}`}
                      className="listings-icon-box"
                    >
                      <PersonOutlinedIcon
                        className="listings-icon-traveler"
                        fontSize="small"
                      />
                    </div>
                    <span className="listings-count">{traveler}</span>
                    <UncontrolledTooltip
                      target={`traveler-${row._id}`}
                      placement="top"
                    >
                      {t("page.users.travelerListings", "Traveler Listing")}: {traveler}
                    </UncontrolledTooltip>
                  </div>
                  <div className="d-flex flex-column align-items-center">
                    <div
                      id={`sender-${row._id}`}
                      className="listings-icon-box"
                    >
                      <img
                        src={senderLogo}
                        alt="sender"
                        className="listings-icon-sender-img"
                      />
                    </div>
                    <span className="listings-count">{sender}</span>
                    <UncontrolledTooltip
                      target={`sender-${row._id}`}
                      placement="top"
                    >
                      {t("page.users.senderListings", "Sender Listing")}: {sender}
                    </UncontrolledTooltip>
                  </div>
                </div>
              );
            },
          },

          {
            text: t("table.headers.actions", "Actions"),
            dataField: "action",
            headerAlign: "left",
            align: "left",
            headerStyle: { textAlign: "left", verticalAlign: "middle" },
            style: { textAlign: "left", verticalAlign: "middle" },
            formatter: (cell, row, rowIndex, formatExtraData) => {
              const statusLoadingId = formatExtraData;
              return (
                <UserActions
                  row={row}
                  onEdit={userActions?.handleEdit}
                  onDelete={userActions?.handleDelete}
                  isLoading={statusLoadingId === row._id}
                />
              );
            },
            formatExtraData: statusLoadingId,
          },
        ]}
      >
        {(props) => (
          <div className="py-4 w-full flex flex-col" ref={componentRef}>
            <div className="px-4 pb-3 d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center">
              <label className="d-flex align-items-center mb-2 mb-md-0">
                <span className="text-sm font-medium text-slate-600 mr-2">
                  {t("table.search", "Search")}:
                </span>
                <div className="position-relative d-flex align-items-center" style={{ width: "100%", maxWidth: "220px" }}>
                  <input
                    className="form-control form-control-sm"
                    placeholder={t("table.search", "Search")}
                    value={localSearch || ""}
                    onChange={(e) => setLocalSearch(e.target.value)}
                    style={{ paddingRight: "24px" }}
                  />
                  {localSearch && (
                    <span
                      onClick={() => {
                        setLocalSearch("");
                        setTableState((prev) => ({ ...prev, search: "", page: 1 }));
                      }}
                      className="position-absolute cursor-pointer search-clear-btn"
                    >
                      <CloseIcon style={{ fontSize: "14px" }} />
                    </span>
                  )}
                </div>
              </label>

              <div className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center mt-2 mt-md-0 ml-md-auto ml-4">
                <div className="listing-header-right d-flex align-items-center">
                  <span className="text-sm font-weight-600 text-muted mr-2">
                    {t("table.filterBy", "Filter By")}
                  </span>
                  <DropdownFilter
                    value={filterType}
                    options={helpers.getFilter("USER_VERIFICATION_STATUS", t)}
                    onChange={(value) => {
                      setFilterType(value);
                      setTableState((prev) => ({ ...prev, page: 1 }));
                    }}
                    buttonClass="btn-neutral filter-select2"
                  />
                  <Button
                    size="sm"
                    className="ml-2 btn-neutral text-danger font-weight-600"
                    onClick={handleClearFilters}
                  >
                    {t("common.clearFilters", "Clear Filters")}
                  </Button>
                </div>
              </div>
            </div>

            <div className="table-responsive">
              <BootstrapTable
                {...props.baseProps}
                bootstrap4
                bordered={false}
                keyField="_id"
                data={dataTable}
                rowClasses={(row) => (isHighlighted("user", row._id) ? "bg-[#F0F7FF] border-left-highlight" : "")}
                remote={{ pagination: true, sort: true }}
                onTableChange={onTableChange}
                defaultSorted={[
                  {
                    dataField: tableState.sortBy || "createdAt",
                    order: tableState.sortOrder === 1 ? "asc" : "desc",
                  },
                ]}
                wrapperClasses="max-h-[60vh] overflow-y-auto"
                noDataIndication={() => (
                  <div className="text-center text-slate-500 py-4 w-full">
                    {isItemsLoading ? t("common.loading", "Loading....") : t("page.users.noUsers", "No users available.")}
                  </div>
                )}
              />
            </div>
            <Pagination
              totalRows={meta?.totalItems || 0}
              rowsPerPage={tableState.limit}
              currentPage={tableState.page - 1}
              onPageChange={(page) =>
                setTableState((prev) => ({
                  ...prev,
                  page: page + 1
                }))
              }
              onRowsPerPageChange={(size) =>
                setTableState((prev) => ({
                  ...prev,
                  limit: size,
                  page: 1
                }))
              }
              t={t}
            />

          </div>
        )}
      </ToolkitProvider>
      <Modal
        isOpen={isRoleModalOpen}
        toggle={closeRoleModal}
        backdrop="static"
        className="modal-dialog-centered"
      >
        {assignRole && (
          <RoleModal
            ticket={assignRole}
            onClose={closeRoleModal}
            onAssigned={(updatedRole) => {
              if (!updatedRole || !assignRole?._id) return;
              setAssignRole((prev) => ({
                ...prev,
                role: updatedRole.name,
              }));
              setItems((prevTable) =>
                prevTable.map((item) =>
                  item._id === assignRole._id
                    ? { ...item, role: updatedRole.name }
                    : item
                )
              );
              closeRoleModal();
            }}
          />
        )}
      </Modal>
      <DeactivateReasonModal
        isOpen={reasonModal}
        toggle={() => {
          setReasonModal(false);
          setSelectedUser(null);
          setStatusLoadingId(null);
        }}
        onConfirm={(reasonText, selectedReason) => {
          if (!selectedUser) return Promise.resolve();

          return handleToggleAccount(
            selectedUser,
            false,
            reasonText,
            selectedReason,
            () => {
              setReasonModal(false);
              setSelectedUser(null);
            }
          );
        }}
      />
    </>
  );
}

export default UsersTable;
